import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, Square, X } from 'lucide-react'
import { useTimerStore } from '../../stores/timer-store'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'

function formatMMSS(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds))
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec.toString().padStart(2, '0')}`
}

export function TimerMiniPlayer() {
  const { t } = useTranslation()
  const phase = useTimerStore((s) => s.phase)
  const dialogOpen = useTimerStore((s) => s.dialogOpen)
  const endModalOpen = useTimerStore((s) => s.endModalOpen)
  const resumePromptOpen = useTimerStore((s) => s.resumePromptOpen)
  const paused = useTimerStore((s) => s.paused)
  const phaseStartedAt = useTimerStore((s) => s.phaseStartedAt)
  const phaseDurationMs = useTimerStore((s) => s.phaseDurationMs)
  const pausedRemainingMs = useTimerStore((s) => s.pausedRemainingMs)
  const templates = useTimerStore((s) => s.templates)
  const templateIdx = useTimerStore((s) => s.templateIdx)
  const setIdx = useTimerStore((s) => s.setIdx)
  const openDialog = useTimerStore((s) => s.openDialog)
  const reset = useTimerStore((s) => s.reset)
  const resumeAfterPrompt = useTimerStore((s) => s.resumeAfterPrompt)
  const resetAfterPrompt = useTimerStore((s) => s.resetAfterPrompt)

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (paused || phase === 'idle' || phase === 'completed' || phaseDurationMs == null) return
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [paused, phase, phaseDurationMs])

  if (phase === 'idle' || phase === 'completed') return null
  if (dialogOpen || endModalOpen) return null

  const tpl = templates[templateIdx]
  if (!tpl) return null

  const remainingMs = (() => {
    if (paused && pausedRemainingMs != null) return pausedRemainingMs
    if (phaseDurationMs == null || phaseStartedAt == null) return null
    return Math.max(0, phaseDurationMs - (now - phaseStartedAt))
  })()

  const progressPct = phaseDurationMs && remainingMs != null
    ? (1 - remainingMs / phaseDurationMs) * 100
    : 0

  const phaseLabel = (() => {
    if (phase === 'work') return t('workoutTimer.phase.work', 'Work')
    if (phase === 'rest') return t('workoutTimer.phase.rest', 'Rest')
    if (phase === 'set_rest') return t('workoutTimer.phase.setRest', 'Rest between sets')
    return ''
  })()

  const isManualWork = phase === 'work' && tpl.manual
  const barColor = phase === 'work' ? 'bg-red-500' : 'bg-blue-500'

  if (resumePromptOpen) {
    return (
      <div className="sticky top-0 z-40 bg-card border-b shadow-sm">
        <div className="container px-4 py-2 flex items-center justify-between gap-2">
          <span className="text-sm">
            {t('workoutTimer.resumePrompt.message', 'Timer was paused. Resume from this template?')}
          </span>
          <div className="flex gap-1">
            <Button type="button" size="sm" onClick={resumeAfterPrompt}>
              <Play className="size-3 mr-1" />
              {t('workoutTimer.resumePrompt.resume', 'Resume')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={resetAfterPrompt}>
              <Square className="size-3 mr-1" />
              {t('workoutTimer.resumePrompt.reset', 'Reset')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={openDialog}
      className="sticky top-0 z-40 w-full bg-card border-b shadow-sm text-left"
      aria-label={t('workoutTimer.miniPlayer.open', 'Open timer')}
    >
      <div className="container px-4 py-2 flex items-center gap-3">
        <span className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
          {phaseLabel}
        </span>
        <span className="text-sm font-medium truncate flex-1">
          {tpl.title ?? t('workoutTimer.untitledTemplate', 'Template')}
          <span className="text-muted-foreground ml-2">
            {t('workoutTimer.setCounter', 'Set {{current}}/{{total}}', {
              current: setIdx + 1,
              total: tpl.sets,
            })}
          </span>
        </span>
        <span className="text-sm font-bold tabular-nums">
          {isManualWork ? '—' : formatMMSS((remainingMs ?? 0) / 1000)}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            reset()
          }}
          className="text-muted-foreground hover:text-foreground"
          aria-label={t('workoutTimer.miniPlayer.stop', 'Stop timer')}
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="h-0.5 w-full bg-muted">
        <div
          className={cn('h-full transition-[width] duration-300 ease-linear', barColor)}
          style={{ width: isManualWork ? '100%' : `${progressPct}%` }}
        />
      </div>
    </button>
  )
}
